import { DateRange, FiltersDefinition, FiltersState } from '@factorialco/f0-react/dist/experimental'
import { AnalyticsReportResultsQuery } from 'generated/resources/graphql'

type Schema =
  AnalyticsReportResultsQuery['iris']['sqlResultsConnection']['nodes'][number]['schema'][number]

function filterForType(column: Schema): FiltersDefinition<string> {
  const label = column.displayName ?? column.name
  if (column.enumValues.length > 0) {
    return {
      [column.name]: {
        type: 'in',
        label,
        options: {
          options: column.enumValues.map((value) => ({ value, label: value })),
        },
      },
    }
  }

  if (column.dataType === 'string' && column.formatHint === 'date') {
    return { [column.name]: { type: 'date', options: { mode: 'range' }, label } }
  }

  if (column.dataType === 'string') {
    return { [column.name]: { type: 'search', label } }
  }

  return {}
}

export function filterDefinitionsFromSchema(schema: Schema[]): FiltersDefinition<string> {
  return schema.map(filterForType).reduce((acc, curr) => ({ ...acc, ...curr }), {})
}

type JsonLogicOperator =
  | 'var'
  | 'missing'
  | 'missing_some'
  | 'if'
  | '=='
  | '==='
  | '!='
  | '!=='
  | '!'
  | '!!'
  | 'or'
  | 'and'
  | '>'
  | '>='
  | '<'
  | '<='
  | 'between'
  | 'max'
  | 'min'
  | '+'
  | '-'
  | '*'
  | '/'
  | '%'
  | 'map'
  | 'filter'
  | 'reduce'
  | 'all'
  | 'none'
  | 'some'
  | 'merge'
  | 'in'
  | 'cat'
  | 'substr'
  | 'log'

type JsonLogicFilter = {
  [K in JsonLogicOperator]?: JsonLogicFilter | unknown | Array<JsonLogicFilter | unknown>
}

type VarExpression = {
  var: string
}

type InExpressionEnum = {
  in: [VarExpression, Array<unknown>]
}

type InExpressionSearch = {
  in: [string, VarExpression]
}

type BetweenExpression = {
  between: [string | Date, VarExpression, string | Date]
}

type AndExpression = {
  and: Array<InExpressionEnum | InExpressionSearch | BetweenExpression>
}

type FilterResult = {
  fieldName: string
  value: unknown
}

type ParseErrorType = 'INVALID_STRUCTURE'

type ParseError = {
  type: ParseErrorType
  message: string
  context?: Record<string, unknown>
}

type ParseResult = { success: true; filter: FilterResult } | { success: false; error: ParseError }

export function datatableFiltersToJsonLogicFilters(
  filters: FiltersState<FiltersDefinition<string>>
): JsonLogicFilter | undefined {
  const conditions = Object.entries(filters)
    .map(([fieldName, filterValue]) => {
      // Skip undefined/null/empty values
      if (filterValue === undefined || filterValue === null || filterValue === '') {
        return null
      }

      // Skip enum filters (arrays) - not implemented
      if (Array.isArray(filterValue)) {
        return null
      }

      // Check for DateRange object: { from: Date, to: Date }
      if (isDateRange(filterValue)) {
        return { between: [filterValue.from, { var: fieldName }, filterValue.to] }
      }

      // Everything else: treat as string search
      return { in: [String(filterValue), { var: fieldName }] }
    })
    .filter((condition): condition is NonNullable<typeof condition> => condition !== null)

  if (conditions.length === 0) {
    return undefined
  }

  return { and: conditions }
}

// ============================================================================
// Type Guards
// ============================================================================

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null
}

const isVarExpression = (value: unknown): value is VarExpression => {
  return isObject(value) && 'var' in value && typeof value.var === 'string'
}

const isInExpressionEnum = (value: unknown): value is InExpressionEnum => {
  if (!isObject(value) || !('in' in value) || !Array.isArray(value.in)) {
    return false
  }
  const [first, second] = value.in
  return value.in.length === 2 && isVarExpression(first) && Array.isArray(second)
}

const isInExpressionSearch = (value: unknown): value is InExpressionSearch => {
  if (!isObject(value) || !('in' in value) || !Array.isArray(value.in)) {
    return false
  }
  const [first, second] = value.in

  // Handle both direct VarExpression and array with single VarExpression
  const varExpr = Array.isArray(second) && second.length === 1 ? second[0] : second

  return value.in.length === 2 && typeof first === 'string' && isVarExpression(varExpr)
}

const isBetweenExpression = (value: unknown): value is BetweenExpression => {
  if (!isObject(value) || !('between' in value) || !Array.isArray(value.between)) {
    return false
  }
  const betweenArray = value.between
  return (
    betweenArray.length === 3 &&
    (typeof betweenArray[0] === 'string' || betweenArray[0] instanceof Date) &&
    isVarExpression(betweenArray[1]) &&
    (typeof betweenArray[2] === 'string' || betweenArray[2] instanceof Date)
  )
}

const isSuccessResult = (
  result: ParseResult
): result is { success: true; filter: FilterResult } => {
  return result.success
}

const isDateRange = (value: unknown): value is DateRange => {
  return (
    isObject(value) &&
    'from' in value &&
    'to' in value &&
    value.from instanceof Date &&
    value.to instanceof Date
  )
}

const extractAndConditions = (
  parsed: unknown
): { success: true; data: unknown[] } | { success: false; error: string } => {
  if (!isObject(parsed) || !('and' in parsed) || !Array.isArray(parsed.and)) {
    return { success: false, error: 'Missing "and" array in JsonLogic structure' }
  }
  return { success: true, data: parsed.and }
}

// ============================================================================
// Pattern Matchers
// ============================================================================

const parseInExpressionEnum = (expression: InExpressionEnum): ParseResult => {
  const fieldName = expression.in[0].var
  const values = expression.in[1]

  const filteredValues = values.filter((v): v is string | number => v !== '')
  if (filteredValues.length === 0) {
    return {
      success: false,
      error: { type: 'INVALID_STRUCTURE', message: 'No valid values provided' },
    }
  }

  return {
    success: true,
    filter: { fieldName, value: filteredValues },
  }
}

const parseInExpressionSearch = (expression: InExpressionSearch): ParseResult => {
  const searchValue = expression.in[0]
  const secondElement = expression.in[1]

  // Handle both direct VarExpression and array with single VarExpression
  const varExpr =
    Array.isArray(secondElement) && secondElement.length === 1 ? secondElement[0] : secondElement
  const fieldName = varExpr.var

  return {
    success: true,
    filter: { fieldName, value: searchValue },
  }
}

const parseBetweenExpression = (expression: BetweenExpression): ParseResult => {
  const [fromDate, varExpr, toDate] = expression.between
  const fieldName = varExpr.var

  try {
    return {
      success: true,
      filter: {
        fieldName,
        value: {
          from: new Date(fromDate),
          to: new Date(toDate),
        },
      },
    }
  } catch (error) {
    return {
      success: false,
      error: {
        type: 'INVALID_STRUCTURE',
        message: `Error parsing dates for column ${fieldName}`,
        context: { fieldName, fromDate, toDate, error: String(error) },
      },
    }
  }
}

const parseCondition = (condition: unknown): ParseResult => {
  if (!isObject(condition)) {
    return {
      success: false,
      error: { type: 'INVALID_STRUCTURE', message: 'Condition is not an object' },
    }
  }

  // Try parsing as InExpression (enum pattern)
  if (isInExpressionEnum(condition)) {
    return parseInExpressionEnum(condition)
  }

  // Try parsing as InExpression (search pattern)
  if (isInExpressionSearch(condition)) {
    return parseInExpressionSearch(condition)
  }

  // Try parsing as BetweenExpression
  if (isBetweenExpression(condition)) {
    return parseBetweenExpression(condition)
  }

  return {
    success: false,
    error: {
      type: 'INVALID_STRUCTURE',
      message: 'Unknown condition pattern',
      context: { condition },
    },
  }
}

// ============================================================================
// Main Function
// ============================================================================

/**
 * Converts saved jsonLogic filters back to OneDataCollection FiltersState format.
 * This is the reverse of datatableFiltersToJsonLogicFilters.
 *
 * @param jsonLogicFilters - JSON string containing jsonLogic filter structure
 * @returns FiltersState object that can be used by OneDataCollection
 */
export function jsonLogicFiltersToFiltersState(
  jsonLogicFilters: string
): FiltersState<FiltersDefinition<string>> {
  if (Object.keys(jsonLogicFilters).length === 0) {
    return {}
  }

  const conditionsResult = extractAndConditions(JSON.parse(jsonLogicFilters))
  if (!conditionsResult.success) {
    throw new Error(`Unexpected jsonLogic structure:': ${conditionsResult.error}`)
  }

  const parseResults = conditionsResult.data
    .map((condition) => parseCondition(condition))
    .filter(isSuccessResult)

  const filtersState: Record<string, string | unknown[] | DateRange | Date | undefined> = {}
  parseResults.forEach((result) => {
    const value = result.filter.value
    if (
      typeof value === 'string' ||
      Array.isArray(value) ||
      value instanceof Date ||
      value === undefined ||
      isDateRange(value)
    ) {
      filtersState[result.filter.fieldName] = value
    }
  })

  return filtersState
}
